import { SettingsService } from "../services/SettingsService";
import { mapToSeatUsage, SeatUsage } from "./organization.model";
import { Setting } from "./settings-model";

export class PlanMetrics {
    HasAccessToEmojis: boolean;
    HasFreeIncome: boolean;
    HasAccessToMMS: boolean;
    HasAccessToNotifications: boolean;
    GroupCreditsUsage: UsageModel;
    NormalTextingUsage: UsageModel;
    EndUsersUsage: UsageModel;
    ReceviedMessages: number;
    Delivered: number;
    HasOverQuota: boolean;
    InGracePeriod: boolean;
    HasAccessAutoReplies: boolean;
    HasMessageScheduling: boolean;
    HasAccessToFaceVault: boolean;
    HasAccessToGroupChat: boolean;
    HasAccessToSecureFiles: boolean;
    HasAccessToSecureChat: boolean;
    HassAccessToAiFeatures: boolean;
    HasAccessToSecureDoc: boolean;
    HasTransferChat: boolean;
    HasAccessToKeywordReplies: boolean;
}

export class UsageModel {
    PlanCredits: number;
    TotalCredits: number;
    UsedCredits: number;
    AddOnCredits: number;
    GetPercentile: () => number;
    GetRemainingCredits: () => number;
}

export enum CreditType {
    PLAN_CREDITS_NORMAL = "pcn",
    PLAN_CREDITS_GROUP = "pcg",
    ADDON_CREDITS_NORMAL = "acn",
    ADDON_CREDITS_GROUP = "acg",
    pcn = "PLAN_CREDITS_NORMAL",
    pcg = "PLAN_CREDITS_GROUP",
    acn = "ADDON_CREDITS_NORMAL",
    acg = "ADDON_CREDITS_GROUP"
}



export const mapToUsageModel = (data: any): UsageModel => {
    return {
        PlanCredits: (data?.total) || 0,
        TotalCredits: (data?.total + data?.addOns) || 0,
        UsedCredits: data?.used || 0,
        AddOnCredits: (data?.addOns) || 0,
        GetRemainingCredits: function () {
            return this.TotalCredits - this.UsedCredits;
        },
        GetPercentile: function () {
            return Math.floor((this.UsedCredits / (this.TotalCredits || 1)) * 100);
        }
    } as UsageModel;
}

function isSettingEnabled(settings: Setting[] | undefined, code: string): boolean {
    const val = settings?.find(x => x.Code === code)?.Value;
    if (typeof val === 'string') return val.toLowerCase() === 'true';
    return !!val === true;
}

export const mapToPlanMetrics = (data: any, settings?: Setting[]): PlanMetrics => {
    return {
        HasAccessToEmojis: !!data?.hasEmojis,
        HasFreeIncome: !!data?.hasFreeIncome,
        HasAccessToMMS: data?.hasMMSEnabled === true ? isSettingEnabled(settings, SettingsService.MMS) : false,
        HasAccessToNotifications: !!data?.hasNotificationsEnabled,
        ReceviedMessages: data?.received || 0,
        GroupCreditsUsage: mapToUsageModel(data?.groupMsgCredits),
        NormalTextingUsage: mapToUsageModel(data?.normalMsgCredits),
        EndUsersUsage: mapToUsageModel(data?.users),
        HasOverQuota: !!data?.hasOverQuota,
        InGracePeriod: !!data?.inGracePeriod,
        Delivered: data?.delivered,
        HasAccessAutoReplies: data?.hasAutoReply === true ? isSettingEnabled(settings, SettingsService.Auto_Replay) : false,
        HasMessageScheduling: data?.hasMessageScheduling === true ? isSettingEnabled(settings, SettingsService.Schedule_Messages) : false,
        HasAccessToFaceVault: true,   // x.hasFaceVault
        HasAccessToGroupChat: isSettingEnabled(settings, SettingsService.Group_Chat),
        HasAccessToSecureFiles: isSettingEnabled(settings, SettingsService.Secure_File),
        HasAccessToSecureChat: isSettingEnabled(settings, SettingsService.Secure_chat),
        HasAccessToSecureDoc: isSettingEnabled(settings, SettingsService.Secure_Doc),
        HasTransferChat: !!data?.hasTransferChat,
        HasAccessToKeywordReplies: !!data?.hasKeyWordReply,
        // HassAccessToAiFeatures: !!data?.hasAiFeature || store.getState().Settings.Settings?.find(x => x.Code === SettingsService.AiFeatures)?.Value?.toLowerCase() === "true"
    } as PlanMetrics;
}

export interface UserPlanTracker {
    OrgPlanId: string;
    UserId: string;
    HasNotificationsEnabled: boolean;
    HasEmojis: boolean;
    HasFreeIncome: boolean;
    NormalMsgCredits: {
        Total: number;
        AddOns: number;
        Used: number;
        AddonSettled: number;
    };
    GroupMsgCredits: {
        Total: number;
        AddOns: number;
        Used: number;
        AddonSettled: number;
    };
    Received: number;
    Delivered: number;
    HasCustomTags: boolean;
    HasOrgwideOptout: boolean;
    HasScheduleReports: boolean;
    HasSecureChats: boolean;
    HasAccessToSecureFiles: boolean;
    HasSharedInbox: boolean;
    HasKeyWordReply: boolean;
    HasAccessAutoReplies: boolean;
    HasLogExports: boolean;
    HasMMSEnabled: boolean;
    HasMessageScheduling: boolean;
    EndDateTime: string;
    HasFaceVault: boolean;
    HasTemplates: boolean;
    HasSignature: boolean;
    HasGroupChat: boolean;
    HasAccessToSecureChat: boolean;
    HasAccessToSecureDoc: boolean;

}


export const mapToUserPlanTracker = (data: any, settings?: Setting[]): UserPlanTracker => {
    return {
        OrgPlanId: data.orgPlanId,
        UserId: data.userId,
        NormalMsgCredits: {
            Total: data.normalMsgCredits?.total ?? 0,
            AddOns: data.normalMsgCredits?.addOns ?? 0,
            Used: data.normalMsgCredits?.used ?? 0,
            AddonSettled: data.normalMsgCredits?.addonSettled ?? 0,
        },
        GroupMsgCredits: {
            Total: data.groupMsgCredits?.total ?? 0,
            AddOns: data.groupMsgCredits?.addOns ?? 0,
            Used: data.groupMsgCredits?.used ?? 0,
            AddonSettled: data.groupMsgCredits?.addonSettled ?? 0,
        },
        Received: data.received,
        Delivered: data.delivered,
        HasCustomTags: data.hasCustomTags,
        HasOrgwideOptout: data.hasOrgwideOptout,
        HasScheduleReports: data.hasScheduleReports,
        HasSecureChats: !!data?.hasSecureChats === true ? isSettingEnabled(settings, SettingsService.Secure_chat) : false,
        HasAccessToSecureFiles: data.hasSecureFileSharing === true ? isSettingEnabled(settings, SettingsService.Secure_File) : false,
        HasSharedInbox: data.hasSharedInbox,
        HasKeyWordReply: data.hasKeyWordReply,
        HasAccessAutoReplies: data?.hasAutoReply === true ? isSettingEnabled(settings, SettingsService.Auto_Replay) : false,
        HasLogExports: data.hasLogExports,
        HasMMSEnabled: data?.hasMMSEnabled === true ? isSettingEnabled(settings, SettingsService.MMS) : false,
        HasMessageScheduling: data?.hasMessageScheduling === true ? isSettingEnabled(settings, SettingsService.Schedule_Messages) : false,
        HasNotificationsEnabled: data.hasNotificationsEnabled,
        HasEmojis: data.hasEmojis,
        HasFreeIncome: data.hasFreeIncome,
        HasFaceVault: data.hasFaceVault,
        EndDateTime: data.endDateTime,
        HasTemplates: data.hasTemplates ?? false,
        HasSignature: data.hasSignature ?? false,
        HasUnlimitedContacts: data.hasUnlimitedContacts,
        HasMessageConfig: data.hasMessageConfig,
        HasGroupBroadcast: data.hasGroupBroadcast,
        HasBlockList: data.hasBlockList,
        HasGroupChat: data.hasGroupChat === true ? isSettingEnabled(settings, SettingsService.Group_Chat) : false,
        HasAccessToSecureChat: !!data?.hasSecureChats === true ? isSettingEnabled(settings, SettingsService.Secure_chat) : false,
        HasAccessToSecureDoc: data.hasFalkonDocs === true ? isSettingEnabled(settings, SettingsService.Secure_Doc) : false,

    } as UserPlanTracker
};

export interface OrgPlanTracker {
    OrgPlanIds: string[];
    SeatUsage: SeatUsage[];
    GroupMsgCredits: {
        Total: number;
        AddOns: number;
        Used: number;
        AddonSettled: number;
        RemainingCredits: number
    };
    EndDateTime: string;
}

export const mapToOrgPlanTracker = (data: any,): OrgPlanTracker => {
    return {
        OrgPlanIds: data.orgPlanIds,
        SeatUsage: data.seatUsage?.map(mapToSeatUsage),
        GroupMsgCredits: {
            Total: data.groupMsgCredits?.total ?? 0,
            AddOns: data.groupMsgCredits?.addOns ?? 0,
            Used: data.groupMsgCredits?.used ?? 0,
            AddonSettled: data.groupMsgCredits?.addonSettled ?? 0,
            RemainingCredits: data.groupMsgCredits?.total - data.groupMsgCredits?.used
        },
        EndDateTime: data.endDateTime,
    };
};


// const check =(isnewOrge, feature)=>{
//     isnewOrge
//     ? feature in plantracker ? true :false

//     : feature in plantracker ?true :false
// }